import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import EventNoteIcon from '@mui/icons-material/EventNote';
import NoteIcon from '@mui/icons-material/Note';
import HistoryIcon from '@mui/icons-material/History';

interface NavItemDef {
  text: string;
  Icon: React.ComponentType<SvgIconProps>;
  path: string;
  isCenter?: boolean;
}

const NAV_ITEMS: NavItemDef[] = [
  { text: 'Home',    Icon: HomeIcon,      path: '/dashboard' },
  { text: 'Tests',   Icon: EventNoteIcon, path: '/upcoming-tests' },
  { text: 'Study',   Icon: SchoolIcon,    path: '/study-setup', isCenter: true },
  { text: 'Notes',   Icon: NoteIcon,      path: '/notes' },
  { text: 'History', Icon: HistoryIcon,   path: '/history' },
];

interface Props {
  collapsed: boolean;
}

export default function MobileBottomNav({ collapsed }: Props) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  const isActive = (path: string) => location.pathname === path;

  const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
  const EASE   = 'cubic-bezier(0.4, 0, 0.2, 1)';

  return (
    <Box
      component="nav"
      aria-label="Mobile navigation"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar + 1,
        backgroundColor: 'background.paper',
        borderTop: '2px solid',
        borderColor: 'primary.main',
        boxShadow: '0 -4px 24px rgba(99,102,241,0.18), 0 -1px 6px rgba(0,0,0,0.08)',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        px: 1,
        py: collapsed ? 0.75 : 1.5,
        transition: `padding ${0.35}s ${SPRING}`,
        overflow: 'visible',
      }}
      style={{
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 12px)',
      }}
    >
      {NAV_ITEMS.map(({ text, Icon, path, isCenter }) => {
        const active = isActive(path);

        /* ── Center Study button ── */
        if (isCenter) {
          return (
            <Box
              key={text}
              role="button"
              tabIndex={0}
              aria-label={text}
              aria-current={active ? 'page' : undefined}
              onClick={() => navigate(path)}
              onKeyDown={(e) => e.key === 'Enter' && navigate(path)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                flex: 1,
                cursor: 'pointer',
                outline: 'none',
                transform: collapsed ? 'translateY(0px)' : 'translateY(-16px)',
                transition: `transform 0.38s ${SPRING}`,
              }}
            >
              <Box
                sx={{
                  width: collapsed ? 44 : 56,
                  height: collapsed ? 44 : 56,
                  borderRadius: '50%',
                  background: active
                    ? 'linear-gradient(145deg, #A5B4FC 0%, #6366F1 40%, #4338CA 100%)'
                    : 'linear-gradient(145deg, #818CF8 0%, #6366F1 45%, #4338CA 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: [
                    `0 ${active ? 10 : 7}px ${active ? 28 : 22}px rgba(99,102,241,${active ? 0.65 : 0.48})`,
                    '0 3px 8px rgba(0,0,0,0.18)',
                    'inset 0 1.5px 0 rgba(255,255,255,0.32)',
                    'inset 0 -1px 0 rgba(0,0,0,0.12)',
                  ].join(', '),
                  transition: `all 0.38s ${SPRING}`,
                  '&:active': {
                    transform: 'scale(0.91)',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.45)',
                  },
                }}
              >
                <Icon sx={{ fontSize: collapsed ? 22 : 26, color: '#fff' }} />
              </Box>

              <Typography
                sx={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  color: active ? 'primary.main' : 'text.secondary',
                  opacity: collapsed ? 0 : 1,
                  maxHeight: collapsed ? '0px' : '16px',
                  overflow: 'hidden',
                  lineHeight: 1,
                  letterSpacing: 0.5,
                  userSelect: 'none',
                  transition: `opacity 0.22s ${EASE}, max-height 0.3s ${EASE}`,
                }}
              >
                {text}
              </Typography>
            </Box>
          );
        }

        /* ── Regular nav item ── */
        return (
          <Box
            key={text}
            role="button"
            tabIndex={0}
            aria-label={text}
            aria-current={active ? 'page' : undefined}
            onClick={() => navigate(path)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(path)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              cursor: 'pointer',
              outline: 'none',
              py: 0.5,
              px: 0.25,
              '&:active > .nav-icon-wrap': {
                transform: 'scale(0.88)',
              },
            }}
          >
            {/* Icon bubble */}
            <Box
              className="nav-icon-wrap"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 42,
                height: 34,
                borderRadius: '14px',
                backgroundColor: active
                  ? isDark
                    ? 'rgba(99,102,241,0.22)'
                    : 'rgba(99,102,241,0.13)'
                  : 'transparent',
                transition: `background-color 0.25s ${EASE}, transform 0.18s ${EASE}`,
              }}
            >
              <Icon
                sx={{
                  fontSize: collapsed ? 20 : 22,
                  color: active ? 'primary.main' : 'text.secondary',
                  transition: `font-size 0.3s ${SPRING}, color 0.2s ${EASE}`,
                }}
              />
            </Box>

            {/* Label */}
            <Typography
              sx={{
                fontSize: '0.62rem',
                fontWeight: active ? 700 : 500,
                color: active ? 'primary.main' : 'text.secondary',
                opacity: collapsed ? 0 : 1,
                maxHeight: collapsed ? '0px' : '16px',
                overflow: 'hidden',
                lineHeight: 1,
                letterSpacing: 0.4,
                userSelect: 'none',
                transition: `opacity 0.22s ${EASE}, max-height 0.3s ${EASE}`,
              }}
            >
              {text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
